import { c as _c } from "react/compiler-runtime";
import { alpha, Box, Card, type SxProps, type Theme, useTheme } from "@mui/material";
import type { PointerEvent as ReactPointerEvent } from "react";
import React, { useCallback, useRef } from "react";
import { useLandingReveal } from "#/features/homePage/ui/landing/useLandingReveal";

/** Keeps `staggerIndex * step` distinct through demo row indices (e.g. 8–11). */
const LANDING_CARD_STAGGER_STEP_MS = 55;
const LANDING_CARD_STAGGER_MAX_MS = 11 * LANDING_CARD_STAGGER_STEP_MS;
export type LandingGlowCardProps = {
  children: React.ReactNode;
  /** Applied to the inner MUI `Card` (layout, padding, etc.). */
  cardSx?: SxProps<Theme>;
  /** Staggered entrance delay index; multiplied by 55ms. */
  staggerIndex?: number;
  /**
   * Demo / link tiles: hover lift, stronger shadow, inner ambient gradient,
   * and flex layout so `CardActionArea` fills the card height.
   */
  interactive?: boolean;
};
export const LandingGlowCard: React.FC<LandingGlowCardProps> = t0 => {
  const $ = _c(56);
  const {
    children,
    cardSx,
    staggerIndex: t1,
    interactive: t2
  } = t0;
  const staggerIndex = t1 === undefined ? 0 : t1;
  const interactive = t2 === undefined ? false : t2;
  const theme = useTheme();
  const delayMs = Math.min(staggerIndex * LANDING_CARD_STAGGER_STEP_MS, LANDING_CARD_STAGGER_MAX_MS);
  const revealRef = useRef(null);
  const rootRef = useRef(null);
  let t3;
  if ($[0] !== delayMs) {
    t3 = {
      staggerMs: delayMs
    };
    $[0] = delayMs;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const {
    revealSx
  } = useLandingReveal(revealRef, t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = event => {
      const el = rootRef.current;
      if (!el) {
        return;
      }
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }
      const pointerXPct = (event.clientX - rect.left) / rect.width * 100;
      const pointerYPct = (event.clientY - rect.top) / rect.height * 100;
      el.style.setProperty("--landing-glow-x", `${pointerXPct}%`);
      el.style.setProperty("--landing-glow-y", `${pointerYPct}%`);
    };
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const updateGlowPosition = t4;
  const accent = theme.appDesign.accentSoft;
  const accentCore = theme.appDesign.accent;
  const muiCardRoot = theme.components?.MuiCard?.styleOverrides?.root;
  const cardBorderRadius = muiCardRoot && typeof muiCardRoot === "object" && "borderRadius" in muiCardRoot && (typeof muiCardRoot.borderRadius === "number" || typeof muiCardRoot.borderRadius === "string") ? muiCardRoot.borderRadius : 8;
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      height: "100%"
    };
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== revealSx) {
    t6 = [t5, revealSx];
    $[4] = revealSx;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = t6 as SxProps<Theme>;
  let t8;
  if ($[6] !== interactive) {
    t8 = {
      ...(interactive ? {
        transition: "transform 0.22s ease",
        "&:hover": {
          transform: "translateY(-3px)"
        }
      } : {})
    };
    $[6] = interactive;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] !== cardBorderRadius || $[9] !== t8) {
    t9 = {
      position: "relative",
      height: "100%",
      borderRadius: cardBorderRadius,
      "--landing-glow-x": "50%",
      "--landing-glow-y": "42%",
      "@media (prefers-reduced-motion: no-preference)": t8
    };
    $[8] = cardBorderRadius;
    $[9] = t8;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== interactive) {
    t10 = interactive ? {
      display: "flex",
      flexDirection: "column" as const
    } : {};
    $[11] = interactive;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== theme.appDesign.outline) {
    t11 = alpha(theme.appDesign.outline, 0.14);
    $[13] = theme.appDesign.outline;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  const t12 = `1px solid ${t11}`;
  let t13;
  if ($[15] !== theme.appDesign.surface) {
    t13 = alpha(theme.appDesign.surface, 0.62);
    $[15] = theme.appDesign.surface;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== theme.appDesign.surfaceHigh) {
    t14 = alpha(theme.appDesign.surfaceHigh, 0.06);
    $[17] = theme.appDesign.surfaceHigh;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  let t15;
  if ($[19] !== theme.appDesign.background) {
    t15 = alpha(theme.appDesign.background, 0.35);
    $[19] = theme.appDesign.background;
    $[20] = t15;
  } else {
    t15 = $[20];
  }
  const t16 = `0 0 0 1px ${t14} inset,
            0 18px 42px ${t15}`;
  let t17;
  if ($[21] !== interactive || $[22] !== theme.appDesign.accentSoft || $[23] !== theme.appDesign.background) {
    t17 = !interactive ? {
      transition: "border-color 0.22s ease, box-shadow 0.22s ease"
    } : {
      "@media (prefers-reduced-motion: no-preference)": {
        transition: "border-color 0.22s ease, box-shadow 0.22s ease",
        ".landing-glow-card-root:hover &": {
          borderColor: alpha(theme.appDesign.accentSoft, 0.32),
          boxShadow: `0 0 0 1px ${alpha(theme.appDesign.accentSoft, 0.12)} inset,
                    0 22px 48px ${alpha(theme.appDesign.background, 0.42)}`
        }
      },
      "@media (prefers-reduced-motion: reduce)": {
        ".landing-glow-card-root:hover &": {
          borderColor: alpha(theme.appDesign.accentSoft, 0.28)
        }
      }
    };
    $[21] = interactive;
    $[22] = theme.appDesign.accentSoft;
    $[23] = theme.appDesign.background;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== cardBorderRadius || $[26] !== cardSx || $[27] !== t10 || $[28] !== t12 || $[29] !== t13 || $[30] !== t16 || $[31] !== t17) {
    t18 = {
      position: "relative",
      zIndex: 0,
      height: "100%",
      ...t10,
      borderRadius: cardBorderRadius,
      overflow: "hidden",
      border: t12,
      bgcolor: t13,
      backdropFilter: "blur(14px) saturate(160%)",
      boxShadow: t16,
      ...t17,
      ...cardSx
    };
    $[25] = cardBorderRadius;
    $[26] = cardSx;
    $[27] = t10;
    $[28] = t12;
    $[29] = t13;
    $[30] = t16;
    $[31] = t17;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[33] !== accent || $[34] !== accentCore || $[35] !== children || $[36] !== interactive) {
    t19 = interactive ? <><Box aria-hidden={true} className="landing-glow-card-ambient" sx={{
        position: "absolute",
        inset: 0,
        zIndex: 0,
        borderRadius: "inherit",
        pointerEvents: "none",
        opacity: 0,
        transition: "opacity 0.4s ease",
        background: `radial-gradient(
                  520px circle at var(--landing-glow-x) var(--landing-glow-y),
                  ${alpha(accent, 0.14)} 0%,
                  ${alpha(accentCore, 0.06)} 42%,
                  transparent 58%
                )`,
        "@media (prefers-reduced-motion: reduce)": {
          opacity: 0,
          transition: "none"
        },
        "@media (prefers-reduced-motion: no-preference)": {
          ".landing-glow-card-root:hover &": {
            opacity: 1
          }
        }
      }} /><Box sx={{
        position: "relative",
        zIndex: 1,
        flex: "1 1 auto",
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        height: "100%",
        "& .MuiCardActionArea-root": {
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch"
        }
      }}>{children}</Box></> : children;
    $[33] = accent;
    $[34] = accentCore;
    $[35] = children;
    $[36] = interactive;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  let t20;
  if ($[38] !== t18 || $[39] !== t19) {
    t20 = <Card elevation={0} sx={t18}>{t19}</Card>;
    $[38] = t18;
    $[39] = t19;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== accent) {
    t21 = alpha(accent, 0.55);
    $[41] = accent;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== accentCore) {
    t22 = alpha(accentCore, 0.22);
    $[43] = accentCore;
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  const t23 = `radial-gradient(
            420px circle at var(--landing-glow-x) var(--landing-glow-y),
            ${t21} 0%,
            ${t22} 38%,
            transparent 68%
          )`;
  let t24;
  let t25;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = {
      opacity: 0,
      transition: "none"
    };
    t25 = {
      ".landing-glow-card-root:hover &": {
        opacity: 1
      }
    };
    $[45] = t24;
    $[46] = t25;
  } else {
    t24 = $[45];
    t25 = $[46];
  }
  let t26;
  if ($[47] !== t23) {
    t26 = <Box aria-hidden={true} className="landing-glow-card-ring" sx={{
      position: "absolute",
      inset: 0,
      zIndex: 1,
      borderRadius: "inherit",
      pointerEvents: "none",
      padding: "1px",
      opacity: 0,
      transition: "opacity 0.35s ease",
      background: t23,
      WebkitMask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      WebkitMaskComposite: "xor",
      mask: "linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)",
      maskComposite: "exclude",
      "@media (prefers-reduced-motion: reduce)": t24,
      "@media (prefers-reduced-motion: no-preference)": t25
    }} />;
    $[47] = t23;
    $[48] = t26;
  } else {
    t26 = $[48];
  }
  let t27;
  if ($[49] !== t20 || $[50] !== t26 || $[51] !== t9) {
    t27 = <Box ref={rootRef} onPointerEnter={updateGlowPosition} onPointerMove={updateGlowPosition} className="landing-glow-card-root" sx={t9}>{t20}{t26}</Box>;
    $[49] = t20;
    $[50] = t26;
    $[51] = t9;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  let t28;
  if ($[53] !== t27 || $[54] !== t7) {
    t28 = <Box ref={revealRef} sx={t7}>{t27}</Box>;
    $[53] = t27;
    $[54] = t7;
    $[55] = t28;
  } else {
    t28 = $[55];
  }
  return t28;
};